import {
  Alert,
  Form,
  FormField,
  FormSubmitHandler,
  Separator,
  showNotification,
} from 'platform/components';
import {Grid, Show, VStack} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';

import {
  GetWarehouseHandlingUnitResponse,
  usePatchHandlingUnitMutation,
  usePostHandlingUnitMutation,
} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SettingsFooter} from '@dms/settings/shared';
import {handleApiError, useDuplicateErrorHandler} from '@dms/shared';

import {ApiError, Nullish, RequiredTestIdProps, suffixTestId, useNavigate} from 'shared/web';

import {MeasurementUnitForm, MeasurementUnitFormSchema} from '../types/MeasurementUnitForm';

export interface MeasurementUnitsDetailFormProps extends RequiredTestIdProps {
  measurementUnit: GetWarehouseHandlingUnitResponse | Nullish;
  isEditDisabled: boolean;
}

export function MeasurementUnitsDetailForm(props: MeasurementUnitsDetailFormProps) {
  const navigate = useNavigate();

  const {duplicateError: isDuplicated, duplicateErrorHandler} = useDuplicateErrorHandler();

  const [postMeasurementUnit] = usePostHandlingUnitMutation();
  const [patchMeasurementUnit] = usePatchHandlingUnitMutation();

  const handleNavigateBack = () => {
    navigate(settingsRoutes.warehousesMeasurementUnits);
  };

  const handleSubmit: FormSubmitHandler<MeasurementUnitForm> = async (
    formValues,
    _setErrors,
    _reset,
    blockerContext
  ) => {
    if (isNotNil(props.measurementUnit)) {
      return await patchMeasurementUnit({
        id: props.measurementUnit.id,
        body: {
          isDefault: formValues.isDefault,
          isActive: props.measurementUnit?.isActive,
          name: formValues.name,
          description: formValues.description,
        },
      })
        .unwrap()
        .then(() => {
          if (blockerContext?.wasSubmittedFromBlocker) {
            return;
          }
          handleNavigateBack();
        })
        .catch((e: ApiError) =>
          handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
        );
    }

    await postMeasurementUnit({
      isDefault: formValues.isDefault,
      name: formValues.name,
      description: formValues.description,
    })
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('entity.warehouse.notifications.measurementUnitCreated'));
        if (blockerContext?.wasSubmittedFromBlocker) {
          return;
        }
        handleNavigateBack();
      })
      .catch(duplicateErrorHandler);
  };

  const defaultValues: Partial<MeasurementUnitForm> = {
    isDefault: props.measurementUnit?.isDefault ?? false,
    isActive: props.measurementUnit?.isActive ?? true,
    name: props.measurementUnit?.name,
    description: props.measurementUnit?.description,
  };

  return (
    <Form<MeasurementUnitForm>
      experimentalZodSchema={MeasurementUnitFormSchema}
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      isDisabled={props.isEditDisabled}
      shouldWatchForUnsavedChanges
    >
      {(control, _formApi, navigateWithoutBlocker) => (
        <VStack spacing={4} data-testid={props['data-testid']}>
          <Show when={isDuplicated}>
            <Alert
              variant="error"
              title={i18n.t('entity.warehouse.notifications.measurementUnitAlreadyExists')}
              data-testid={suffixTestId('measurementUnitAlreadyExists', props)}
            />
          </Show>
          <Grid columns={1}>
            <FormField
              isDisabled
              type="switch"
              name="isDefault"
              label={i18n.t('general.labels.default')}
              control={control}
              data-testid={suffixTestId('inputs.default', props)}
            />
          </Grid>
          <Grid columns={1}>
            <FormField
              isDisabled
              type="switch"
              name="isActive"
              label={i18n.t('general.labels.active')}
              control={control}
              data-testid={suffixTestId('inputs.active', props)}
            />
          </Grid>
          <Separator />
          <Grid columns={2}>
            <FormField
              type="text"
              name="name"
              label={i18n.t('entity.warehouse.labels.name')}
              control={control}
              isRequired
              data-testid={suffixTestId('inputs.name', props)}
            />
            <FormField
              type="text"
              name="description"
              label={i18n.t('entity.warehouse.labels.description')}
              control={control}
              data-testid={suffixTestId('inputs.description', props)}
            />
          </Grid>
          <SettingsFooter
            actions={[
              {
                type: 'button',
                variant: 'secondary',
                title: isNil(props.measurementUnit)
                  ? i18n.t('general.actions.discard')
                  : i18n.t('general.actions.discardChanges'),
                onClick: () => navigateWithoutBlocker(settingsRoutes.warehousesMeasurementUnits),
                'data-testid': suffixTestId('actions.discard', props),
              },
              {
                type: 'form-button',
                control,
                buttonType: 'submit',
                variant: 'primary',
                title: isNil(props.measurementUnit)
                  ? i18n.t('general.actions.save')
                  : i18n.t('general.actions.saveChanges'),
                'data-testid': suffixTestId('actions.submit', props),
              },
            ]}
          />
        </VStack>
      )}
    </Form>
  );
}
